import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  CircularProgress,
  Checkbox,
  Avatar,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { useMessage } from '../../context/MessageContext';
import { useApp } from '../../context/AppContext';
import { 
  createNote, 
  getAllNotes, 
  updateNote, 
  deleteNote, 
  getNotesStats 
} from '../../apis/apiList/notesApi';
import { 
  createReminder, 
  getAllReminders, 
  updateReminder, 
  deleteReminder, 
  getRemindersStats,
  markReminderCompleted,
  markReminderIncomplete 
} from '../../apis/apiList/remindersApi';
import { getUsersByOrgId, getProductionTeam } from '../../apis/apiList/userApi';

const NotesAndReminder = ({orgId}) => {
  // Context hooks
  const { showMessage } = useMessage();
  const { userDetails } = useApp();

  // State management
  const [notes, setNotes] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [openNoteDialog, setOpenNoteDialog] = useState(false);
  const [openReminderDialog, setOpenReminderDialog] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editingReminder, setEditingReminder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [reminderTotalItems, setReminderTotalItems] = useState(0);
  const [fetchFirstTime, setFetchFirstTime] = useState(true);
  // View mode state
  const [viewMode, setViewMode] = useState('grid'); // 'list' or 'grid'
  
  // Detail modal state
  const [detailModal, setDetailModal] = useState({ open: false, item: null, type: null });

  // Loading states
  const [loading, setLoading] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);

  // Form state for notes
  const [noteFormData, setNoteFormData] = useState({
    title: '',
    content: ''
  });

  // Form state for reminders
  const [reminderFormData, setReminderFormData] = useState({
    title: '',
    description: '',
    reminderDate: null,
    reminderTime: null,
    assignedTo: []
  });
  
  // State for organization users
  const [orgUsers, setOrgUsers] = useState([]);

  // Load data from API
  const loadNotes = async () => {
    try {
      setNoteLoading(true);
      const params = {
        organizationId: orgId,
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        sortOrder: 'desc'
      };
      
      const response = await getAllNotes(params, {
        headers: { "x-access-token": userDetails.jwtToken },
      });
      setNotes(response.data || []);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalItems(response.pagination?.totalNotes || 0);
    } catch (error) {
      console.error('Error loading notes:', error);
      showMessage('Failed to load notes', 'error');
    } finally {
      setNoteLoading(false);
    }
  };

  const loadReminders = async () => {
    try {
      setReminderLoading(true);
      const params = {
        organizationId: orgId,
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        sortBy: 'reminderTime',
        sortOrder: 'asc',
        // We're showing all reminders regardless of completion status
        // If you want to filter by completion status, uncomment the line below
        // isCompleted: false
      };
      
      const response = await getAllReminders(params, {
        headers: { "x-access-token": userDetails.jwtToken },
      });
      setReminders(response.data || []);
      setTotalPages(response.pagination?.totalPages || 1);
      setReminderTotalItems(response.pagination?.totalReminders || 0);
    } catch (error) {
      console.error('Error loading reminders:', error);
      showMessage('Failed to load reminders', 'error');
    } finally {
      setReminderLoading(false);
    }
  };

  // Load organization users
  const loadOrgUsers = async () => {
    try {
      // const response = await getUsersByOrgId(orgId);
      const response = await getProductionTeam(orgId);

      // setOrgUsers(response.data || []);
      // let tempData = [
      //   {
      //     _id: "67c6de118da97a3355861dee",
      //     firstName: "Jim",
      //     lastName: "Wacksman",
      //     email: "jim@associationstudios.com",
      //     password: "$2b$10$6kCH1SfZ.j9ptHangQMOIeN4l.MjjhOZR8RIDGt4dbMV8GVnS6Un2",
      //     role: "admin",
      //     isActive: true,
      //     createdAt: {
      //       $date: "2025-01-29T10:56:01.699Z"
      //     },
      //     updatedAt: {
      //       $date: "2025-09-11T15:18:43.731Z"
      //     },
      //     __v: 2,
      //     jwtToken: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjY3YzZkZTExOGRhOTdhMzM1NTg2MWRlZSIsImlhdCI6MTc1NzYwMzkyMywiZXhwIjoxNzU4MDM1OTIzfQ.I4VYyzN7iIoQYdIwDMx7V3-THcnK17RduYdZAak7qus",
      //     userslikedorganization: [
      //       "testing for box"
      //     ],
      //     slackUserId: "U08GR3UPXC0",
      //     isSlackWorkspaceJoined: true,
      //     hasSlackAccount: true,
      //     isSlackOauthCompleted: false,
      //     jobTitle: "admin",
      //     orgId: {
      //       $oid: "67ee7df84174bc43f14fd40f"
      //     },
      //     username: "Mr.Wacksman",
      //     linkedin: "undefined",
      //     notes: "undefined",
      //     organization: "testing for box",
      //     phone: "",
      //     profilePicture: "1850505230172",
      //     twitter: "undefined",
      //     type: "vpmauser",
      //     resetPasswordExpire: {
      //       $date: "2025-05-02T11:08:25.507Z"
      //     },
      //     resetPasswordToken: "2c800bbef254ffdb6b43c076917c8bc907edd5d23aa6958c2669cf50830f2ea1",
      //     vpmaUserId: "26",
      //     ratePerProject: 0,
      //     slackUserToken: "xoxp-8546978857650-8569130813408-8601463300737-63730b5f2006e72415835769dca5a93f"
      //   },
      //   {
      //     _id:  "68021a546f335eac570b9348",
      //     firstName: "Darcy",
      //     lastName: "Mehta",
      //     username: "darcy_mehta",
      //     email: "darcy@associationstudios.com",
      //     password: "$2b$10$pRd2E271Ty2QPDSkjd9xHOzmW4EGqa53SpnpnAPRu3tV4CSrp1iQ6",
      //     profilePicture: "1850460282119",
      //     role: "admin",
      //     isActive: true,
      //     orgId: {
      //       $oid: "67ee7df84174bc43f14fd40f"
      //     },
      //     phone: "",
      //     jobTitle: "Actor",
      //     linkedin: "",
      //     twitter: "",
      //     notes: "",
      //     type: "vpmauser",
      //     userslikedorganization: [
      //       "testing for box"
      //     ],
      //     isSlackWorkspaceJoined: false,
      //     isSlackOauthCompleted: false,
      //     hasSlackAccount: true,
      //     createdAt: {
      //       $date: "2025-04-18T09:24:36.417Z"
      //     },
      //     updatedAt: {
      //       $date: "2025-09-12T00:12:53.261Z"
      //     },
      //     __v: 0,
      //     vpmaUserId: "16039",
      //     jwtToken: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjY4MDIxYTU0NmYzMzVlYWM1NzBiOTM0OCIsImlhdCI6MTc1NzYyODM5MiwiZXhwIjoxNzU4MDYwMzkyfQ.lS_X0yDlZVMA9zVTC5X0Xx-gEtFfzRsXuVZSUmiv8SQ",
      //     organization: "",
      //     userSlackEmail: "darcy@associationstudios.com",
      //     slackUserId: "U092E4FFDSB",
      //     ratePerProject: 0,
      //     resetPasswordExpire: null,
      //     resetPasswordToken: null
      //   }
      // ]
      // setOrgUsers(tempData || []);
      setOrgUsers(response.data || []);
    } catch (error) {
      console.error('Error loading organization users:', error);
      showMessage('Failed to load organization users', 'error');
    }
  };

  // Load data on component mount and when dependencies change
  useEffect(() => {
    if (activeTab === 0) {
      loadNotes();
      if(fetchFirstTime){
        loadReminders();
        loadOrgUsers(); // Load users when component mounts
        setFetchFirstTime(false)
      }
    } else {
      loadReminders();
    }
  }, [activeTab, currentPage, searchTerm]);
  
  // Load users when component mounts
  useEffect(() => {
    loadOrgUsers();
  }, []);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Get current page items (API already handles pagination)
  const currentNotes = notes;
  const currentReminders = reminders;

  // Handle note form submission
  const handleNoteSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      let noteData = {
        ...noteFormData,
        organizationId: orgId
      }

      if (editingNote) {
        await updateNote(editingNote._id, noteData, {
            headers: { "x-access-token": userDetails.jwtToken },
          });
        showMessage('Note updated successfully!', 'success');
      } else {
        await createNote(noteData, {
            headers: { "x-access-token": userDetails.jwtToken },
          });
        showMessage('Note created successfully!', 'success');
      }
      
      handleCloseNoteDialog();
      loadNotes(); // Reload notes
    } catch (error) {
      console.error('Error saving note:', error);
      showMessage(error?.response?.data?.message || 'Failed to save note', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle reminder form submission
  // const handleReminderSubmit = async (e) => {
  //   e.preventDefault();
    
  //   try {
  //     setLoading(true);
      
  //     // Format date and time for API
  //     const reminderData = {
  //       ...reminderFormData,
  //       organizationId: orgId,
  //       reminderDate: reminderFormData.reminderDate
  //         ? reminderFormData.reminderDate.toLocaleDateString('en-CA') // YYYY-MM-DD
  //         : null,
  //       reminderTime: reminderFormData.reminderTime
  //         ? reminderFormData.reminderTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  //         : null,
  //     };
      
  //     if (editingReminder) {
  //       await updateReminder(editingReminder._id, reminderData, {
  //           headers: { "x-access-token": userDetails.jwtToken },
  //         });
  //       showMessage('Reminder updated successfully!', 'success');
  //     } else {
  //       await createReminder(reminderData, {
  //           headers: { "x-access-token": userDetails.jwtToken },
  //         });
  //       showMessage('Reminder created successfully!', 'success');
  //     }
      
  //     handleCloseReminderDialog();
  //     loadReminders(); // Reload reminders
  //   } catch (error) {
  //     console.error('Error saving reminder:', error);
  //     showMessage(error?.response?.data?.message || 'Failed to save reminder', 'error');
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  // Handle reminder form submission
  // handleReminderSubmit (updated)
  const handleReminderSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { reminderDate, reminderTime, title, description, assignedTo } = reminderFormData;

      let reminderDateTimeISO = null;

      if (reminderDate && reminderTime) {
        const year = reminderDate.getFullYear();
        const month = reminderDate.getMonth(); // 0-based
        const day = reminderDate.getDate();
        const hours = reminderTime.getHours();
        const minutes = reminderTime.getMinutes();

        // ✅ Correct local combine (no UTC shift bug)
        const combinedLocal = new Date(year, month, day, hours, minutes, 0, 0);
        reminderDateTimeISO = combinedLocal.toISOString();
      }

      // Extract user IDs from the assignedTo array
      const assignedToIds = assignedTo && assignedTo.length > 0 
        ? assignedTo.map(user => user._id) 
        : [];

      const payload = {
        title,
        description,
        reminderDateTime: reminderDateTimeISO,
        organizationId: orgId,
        assignedTo: assignedToIds
      };

      if (editingReminder) {
        // payload.isCompleted = true;
        await updateReminder(editingReminder._id, payload, {
            headers: { "x-access-token": userDetails.jwtToken },
        });
        showMessage('Reminder updated successfully!', 'success');
      } else {
        await createReminder(payload, {
            headers: { "x-access-token": userDetails.jwtToken },
        });
        showMessage('Reminder created successfully!', 'success');
      }

      handleCloseReminderDialog();
      loadReminders();
    } catch (err) {
      console.error(err);
      showMessage(err?.response?.data?.message || 'Failed to save reminder', 'error');
    } finally {
      setLoading(false);
    }
  };


  // Handle dialog operations
  const handleOpenNoteDialog = (note = null) => {
    if (note) {
      setEditingNote(note);
      setNoteFormData({
        title: note.title,
        content: note.content,
      });
    } else {
      setEditingNote(null);
      setNoteFormData({
        title: '',
        content: ''
      });
    }
    setOpenNoteDialog(true);
  };

  // const handleOpenReminderDialog = (reminder = null) => {
  //   if (reminder) {
  //     setEditingReminder(reminder);
  //     setReminderFormData({
  //       title: reminder.title,
  //       description: reminder.description,
  //       reminderDate: reminder.reminderDate ? new Date(reminder.reminderDate) : null,
  //       reminderTime: reminder.reminderTime ? new Date(reminder.reminderTime) : null
  //     });
  //   } else {
  //     setEditingReminder(null);
  //     setReminderFormData({
  //       title: '',
  //       description: '',
  //       reminderDate: null,
  //       reminderTime: null
  //     });
  //   }
  //   setOpenReminderDialog(true);
  // };
  const handleOpenReminderDialog = (reminder = null) => {
    if (reminder) {
      setEditingReminder(reminder);

      let reminderDateObj = null;
      let reminderTimeObj = null;

      if (reminder.reminderDateTime) {
        // DB stores ISO -> new Date(...) gives correct local Date object
        const dt = new Date(reminder.reminderDateTime);
        reminderDateObj = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
        reminderTimeObj = dt; // time picker can use full Date (time part used)
      } else {
        // fallback to legacy fields
        if (reminder.reminderDate) {
          const [y, m, d] = reminder.reminderDate.split('-').map(Number);
          reminderDateObj = new Date(y, m - 1, d);
        }
        if (reminder.reminderDate && reminder.reminderTime) {
          const [y, m, d] = reminder.reminderDate.split('-').map(Number);
          const [hh, mm] = reminder.reminderTime.split(':').map(Number);
          reminderTimeObj = new Date(y, m - 1, d, hh, mm);
        }
      }
      
      // Find the full user objects from orgUsers that match the assignedTo IDs
      const assignedUsers = reminder.assignedTo && reminder.assignedTo.length > 0
        ? orgUsers.filter(user => 
            reminder.assignedTo.some(assigned => 
              (assigned._id && assigned._id === user._id) || 
              (typeof assigned === 'string' && assigned === user._id)
            )
          )
        : [];

      setReminderFormData({
        title: reminder.title || '',
        description: reminder.description || '',
        reminderDate: reminderDateObj,
        reminderTime: reminderTimeObj,
        assignedTo: assignedUsers
      });
    } else {
      setEditingReminder(null);
      setReminderFormData({
        title: '',
        description: '',
        reminderDate: null,
        reminderTime: null,
        assignedTo: []
      });
    }
    setOpenReminderDialog(true);
  };

  const handleCloseNoteDialog = () => {
    setOpenNoteDialog(false);
    setEditingNote(null);
    setNoteFormData({ title: '', content: '' });
  };

  const handleCloseReminderDialog = () => {
    setOpenReminderDialog(false);
    setEditingReminder(null);
    setReminderFormData({ title: '', description: '', reminderDate: null, reminderTime: null });
  };

  // Handle delete operations
  const handleDeleteNote = async (noteId) => {
    try {
      await deleteNote(noteId, {
        headers: { "x-access-token": userDetails.jwtToken },
      });
      showMessage('Note deleted successfully!', 'success');
      loadNotes(); // Reload notes
    } catch (error) {
      console.error('Error deleting note:', error);
      showMessage(error?.response?.data?.message || 'Failed to delete note', 'error');
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    try {
      await deleteReminder(reminderId, {
        headers: { "x-access-token": userDetails.jwtToken },
      });
      showMessage('Reminder deleted successfully!', 'success');
      loadReminders(); // Reload reminders
    } catch (error) {
      console.error('Error deleting reminder:', error);
      showMessage(error?.response?.data?.message || 'Failed to delete reminder', 'error');
    }
  };

  // Handle toggling reminder complete/incomplete status
  const handleToggleReminderComplete = async (reminderId, isCompleted) => {
    try {
      // Immediately update the local state for instant feedback
      if (detailModal.open && detailModal.item._id === reminderId) {
        // Update the detail modal item if it's the same reminder
        setDetailModal(prev => ({
          ...prev,
          item: {
            ...prev.item,
            isCompleted: isCompleted
          }
        }));
      }
      
      // Also update in the reminders list for instant UI feedback
      setReminders(prev => prev.map(reminder => 
        reminder._id === reminderId 
          ? { ...reminder, isCompleted: isCompleted } 
          : reminder
      ));
      
      // Make the API call
      if (isCompleted) {
        await markReminderCompleted(reminderId, {}, {
          headers: { "x-access-token": userDetails.jwtToken },
        });
        showMessage('Reminder marked as complete!', 'success');
      } else {
        await markReminderIncomplete(reminderId, {}, {
          headers: { "x-access-token": userDetails.jwtToken },
        });
        showMessage('Reminder marked as incomplete!', 'success');
      }
      
      // Still reload reminders to ensure data consistency with server
      loadReminders();
    } catch (error) {
      console.error('Error updating reminder status:', error);
      showMessage(error?.response?.data?.message || 'Failed to update reminder status', 'error');
      
      // Revert the optimistic update if the API call fails
      loadReminders();
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  // Handle search with debounce
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle view mode change
  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  // Handle open detail modal
  const handleOpenDetailModal = (item, type) => {
    console.log('item', item)
    setDetailModal({ open: true, item, type });
  };

  // Handle close detail modal
  const handleCloseDetailModal = () => {
    setDetailModal({ open: false, item: null, type: null });
  };

  // Check if content is long (more than 100 characters)
  const isContentLong = (content) => {
    return content && content.length > 100;
  };

  // Get truncated content
  const getTruncatedContent = (content, maxLength = 100) => {
    if (!content) return '';
    return content.length > maxLength ? content.substring(0, maxLength) + '...' : content;
  };

  // Render note card (for both list and grid view)
  const renderNoteCard = (note) => {
    const contentIsLong = isContentLong(note.content);
    const displayContent = getTruncatedContent(note.content);

    return (
      <Card key={note._id} sx={{ 
        border: '1px solid #e0e0e0',
        height: viewMode === 'grid' ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 2,
          borderColor: '#d10000'
        }
      }}
      onClick={() => handleOpenDetailModal(note, 'note')}
      >
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" component="h3" fontWeight="bold" sx={{ 
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: viewMode === 'grid' ? 2 : 1,
              WebkitBoxOrient: 'vertical'
            }}>
              {note.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
              <Tooltip title="Edit">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenNoteDialog(note);
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteNote(note._id);
                  }} 
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          <Box sx={{ mb: 2, flex: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ 
              mb: contentIsLong ? 1 : 0,
              wordBreak: 'break-word'
            }}>
              {displayContent}
            </Typography>
            
            {contentIsLong && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title="View full details">
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenDetailModal(note, 'note');
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  View details
                </Typography>
              </Box>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
            <Typography variant="caption" color="text.secondary">
              {format(new Date(note.updatedAt), 'MMM dd, yyyy')}
            </Typography>
            {viewMode === 'grid' && (
              <Chip 
                label="Note" 
                size="small" 
                variant="outlined" 
                sx={{ fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Render reminder card (for both list and grid view)
  const renderReminderCard = (reminder) => {
    const contentIsLong = isContentLong(reminder.description);
    const displayContent = getTruncatedContent(reminder.description);

    return (
      <Card key={reminder._id} sx={{ 
        border: '1px solid #e0e0e0',
        height: viewMode === 'grid' ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 2,
          borderColor: '#d10000'
        }
      }}
      onClick={() => handleOpenDetailModal(reminder, 'reminder')}
      >
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignItems: 'baseline' }}>
              <Typography variant="h6" component="h3" fontWeight="bold" sx={{ 
                mb: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: viewMode === 'grid' ? 2 : 1,
                WebkitBoxOrient: 'vertical',
                textDecoration: reminder.isCompleted ? 'line-through' : 'none',
                color: reminder.isCompleted ? 'text.secondary' : 'text.primary'
              }}>
                {reminder.title.charAt(0).toUpperCase() + reminder.title.slice(1)}
              </Typography>
              {reminder.assignedTo && reminder.assignedTo.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {reminder.assignedTo.slice(0, 3).map((user, index) => (
                    <Tooltip key={index} title={`${user.firstName || ''} ${user.lastName || ''}`}>
                      <Avatar sx={{ width: 20, height: 20, fontSize: '0.75rem' }}>
                        {user.firstName ? user.firstName.charAt(0) : ''}
                      </Avatar>
                    </Tooltip>
                  ))}
                  {reminder.assignedTo.length > 3 && (
                    <Tooltip title={`${reminder.assignedTo.length - 3} more users`}>
                      <Avatar sx={{ width: 20, height: 20, fontSize: '0.75rem', bgcolor: 'grey.400' }}>
                        +{reminder.assignedTo.length - 3}
                      </Avatar>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
              <Tooltip title={reminder.isCompleted ? "Mark as incomplete" : "Mark as complete"}>
                <Checkbox
                  checked={reminder.isCompleted || false}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleReminderComplete(reminder._id, !reminder.isCompleted);
                  }}
                  sx={{
                    color: '#d10000',
                    '&.Mui-checked': {
                      color: '#d10000',
                    },
                  }}
                />
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenReminderDialog(reminder);
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteReminder(reminder._id);
                  }} 
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          <Box sx={{ mb: 1, flex: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ 
              mb: contentIsLong ? 1 : 0,
              wordBreak: 'break-word',
              textTransform: 'capitalize',
              textDecoration: reminder.isCompleted ? 'line-through' : 'none',
              opacity: reminder.isCompleted ? 0.7 : 1
            }}>
              {displayContent}
            </Typography>
            
            {contentIsLong && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title="View full details">
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenDetailModal(reminder, 'reminder');
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  View details
                </Typography>
              </Box>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
            {/* <Typography variant="caption" color="text.secondary">
              {reminder.reminderTime && 
                format(new Date(reminder.reminderTime), 'MMM dd, yyyy - h:mm a')
                new Date(reminder.reminderDateTime).toLocaleString()
                new Date(reminder.reminderDateTime).toLocaleString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })
              }
            </Typography> */}
            
            <Typography variant="caption" color="text.secondary">
              {reminder.reminderDateTime &&
                new Date(reminder.reminderDateTime).toLocaleString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })
              }
            </Typography>
            {viewMode === 'grid' && (
              <Chip 
                label="Reminder" 
                size="small" 
                variant="outlined" 
                color="primary"
                sx={{ fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box >
      {/* Header */}
      {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Notes & Reminders
        </Typography>
      </Box> */}

      {/* Search Bar and Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            placeholder="Search notes and reminders..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="list">
              <ViewListIcon />
            </ToggleButton>
            <ToggleButton value="grid">
              <ViewModuleIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label={`Notes (${totalItems})`} />
          <Tab label={`Reminders (${reminderTotalItems})`} />
        </Tabs>
      </Box>

      {/* Notes Tab */}
      {activeTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Showing {currentNotes.length} of {totalItems} notes
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenNoteDialog()}
              disabled={loading}
              sx={{ 
                backgroundColor: '#d10000',
                '&:hover': { backgroundColor: '#b80000' }
              }}
            >
              Add Note
            </Button>
          </Box>

          {noteLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : currentNotes.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No notes found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Create your first note to get started
              </Typography>
            </Paper>
          ) : (
            <>
              {viewMode === 'list' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {currentNotes.map(note => renderNoteCard(note))}
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {currentNotes.map(note => (
                    <Grid item xs={12} sm={6} md={4} key={note._id}>
                      {renderNoteCard(note)}
                    </Grid>
                  ))}
                </Grid>
              )}
              
              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Reminders Tab */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Showing {currentReminders.length} of {reminderTotalItems} reminders
            </Typography>
            <Button
              variant="contained"
              startIcon={<NotificationsIcon />}
              onClick={() => handleOpenReminderDialog()}
              disabled={loading}
              sx={{ 
                backgroundColor: '#d10000',
                '&:hover': { backgroundColor: '#b80000' }
              }}
            >
              Add Reminder
            </Button>
          </Box>

          {reminderLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : currentReminders.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No reminders found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Create your first reminder to get started
              </Typography>
            </Paper>
          ) : (
            <>
              {viewMode === 'list' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {currentReminders.map(reminder => renderReminderCard(reminder))}
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {currentReminders.map(reminder => (
                    <Grid item xs={12} sm={6} md={4} key={reminder._id}>
                      {renderReminderCard(reminder)}
                    </Grid>
                  ))}
                </Grid>
              )}
              
              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Add/Edit Note Dialog */}
      <Dialog open={openNoteDialog} onClose={handleCloseNoteDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingNote ? 'Edit Note' : 'Add New Note'}
        </DialogTitle>
        <form onSubmit={handleNoteSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={noteFormData.title}
                  onChange={(e) => setNoteFormData({ ...noteFormData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Content"
                  multiline
                  rows={4}
                  value={noteFormData.content}
                  onChange={(e) => setNoteFormData({ ...noteFormData, content: e.target.value })}
                  required
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseNoteDialog} disabled={loading}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading}
              sx={{ backgroundColor: '#d10000', '&:hover': { backgroundColor: '#b80000' } }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : (editingNote ? 'Update' : 'Create')} Note
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Add/Edit Reminder Dialog */}
      <Dialog open={openReminderDialog} onClose={handleCloseReminderDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingReminder ? 'Edit Reminder' : 'Add New Reminder'}
        </DialogTitle>
        <form onSubmit={handleReminderSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={reminderFormData.title}
                  onChange={(e) => setReminderFormData({ ...reminderFormData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={reminderFormData.description}
                  onChange={(e) => setReminderFormData({ ...reminderFormData, description: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  id="assigned-users"
                  options={orgUsers}
                  getOptionLabel={(option) => `${option.firstName || ''} ${option.lastName || ''}`}
                  value={reminderFormData.assignedTo || []}
                  onChange={(event, newValue) => {
                    setReminderFormData({ ...reminderFormData, assignedTo: newValue });
                  }}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Assign To"
                      placeholder="Select users"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24 }}>
                          {option.firstName ? option.firstName.charAt(0) : ''}
                        </Avatar>
                        {`${option.firstName || ''} ${option.lastName || ''}`}
                      </Box>
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Reminder Date
                  </Typography>
                  <DatePicker
                    selected={reminderFormData.reminderDate}
                    onChange={(date) => setReminderFormData({ ...reminderFormData, reminderDate: date })}
                    dateFormat="MMM dd, yyyy"
                    placeholderText="Select date"
                    className="form-control"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Reminder Time
                  </Typography>
                  <DatePicker
                    selected={reminderFormData.reminderTime}
                    onChange={(time) => setReminderFormData({ ...reminderFormData, reminderTime: time })}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    placeholderText="Select time"
                    className="form-control"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseReminderDialog} disabled={loading}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading}
              sx={{ backgroundColor: '#d10000', '&:hover': { backgroundColor: '#b80000' } }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : (editingReminder ? 'Update' : 'Create')} Reminder
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Detail Modal */}
      <Dialog 
        open={detailModal.open} 
        onClose={handleCloseDetailModal} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          {detailModal.type === 'note' ? (
            <>
              <EditIcon color="primary" />
              <Typography variant="h6" component="span">
                Note Details
              </Typography>
            </>
          ) : (
            <>
              <NotificationsIcon color="primary" />
              <Typography variant="h6" component="span">
                Reminder Details
              </Typography>
            </>
          )}
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          {detailModal.item && (
            <Box sx={{marginTop :'10px'}}>
              <Typography variant="h5" component="h2" fontWeight="bold" sx={{ mb: 2, textTransform: 'Capitalize' }}>
                {detailModal.item.title}
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'Capitalize' }}>
                  {detailModal.type === 'note' ? 'Content:' : 'Description:'}
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="body1" sx={{ 
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.6
                  }}>
                    {detailModal.type === 'note' ? detailModal.item.content : detailModal.item.description}
                  </Typography>
                </Paper>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Created:
                  </Typography>
                  <Typography variant="body2">
                    {format(new Date(detailModal.item.createdAt), 'MMM dd, yyyy - h:mm a')}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Last Updated:
                  </Typography>
                  <Typography variant="body2">
                    {format(new Date(detailModal.item.updatedAt || detailModal.item.createdAt), 'MMM dd, yyyy - h:mm a')}
                  </Typography>
                </Grid>
                
                {detailModal.type === "reminder" && (
                  <>
                    {detailModal.item.reminderDateTime && (
                      <Grid item xs={12} sx={{marginTop :'10px'}}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Reminder Date & Time:
                        </Typography>
                        <Typography variant="body2" color="primary" fontWeight="medium">
                          {format(new Date(detailModal.item.reminderDateTime), 'EEEE, MMMM dd, yyyy - h:mm a')}
                        </Typography>
                      </Grid>
                    )}
                    {detailModal.type === 'reminder' && detailModal.item.assignedTo && detailModal.item.assignedTo.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Assigned To:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {detailModal.item.assignedTo.map((user, index) => (
                            <Chip
                              key={index}
                              avatar={<Avatar>{user.firstName ? user.firstName.charAt(0) : ''}</Avatar>}
                              label={`${user.firstName || ''} ${user.lastName || ''}`}
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}
                    <Grid item xs={12} sx={{paddingTop :'0px'}}>
                      <Box sx={{ display: 'flex', alignItems: 'center', }}>
                        <Checkbox
                          checked={detailModal.item.isCompleted || false}
                          onChange={() => handleToggleReminderComplete(detailModal.item._id, !detailModal.item.isCompleted)}
                          sx={{
                            color: '#d10000',
                            '&.Mui-checked': {
                              color: '#d10000',
                            },
                          }}
                        />
                        <Typography variant="subtitle1">
                          {detailModal.item.isCompleted ? 'Completed' : 'Mark as complete'}
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleCloseDetailModal} variant="outlined">
            Close
          </Button>
          {detailModal.item && (
            <Button 
              onClick={() => {
                handleCloseDetailModal();
                if (detailModal.type === 'note') {
                  handleOpenNoteDialog(detailModal.item);
                } else {
                  handleOpenReminderDialog(detailModal.item);
                }
              }}
              variant="contained"
              startIcon={<EditIcon />}
              sx={{ 
                backgroundColor: '#d10000',
                '&:hover': { backgroundColor: '#b80000' }
              }}
            >
              Edit
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotesAndReminder;
